import PostsGrid from 'Modules/Plans/components/PlatformPosts/PostsGrid/PostsGrid';
import PlatformPost from 'Modules/Plans/components/PlatformPosts/PlatformPost/PlatformPost';
import { mapTikTokPostToPlatformPostData } from 'Modules/Plans/components/PlatformPosts/PlatformPost/helpers';
import useSinglePlayback from 'Hooks/vidstack/useSinglePlayback';
import PlatformPostsFilters, {
    Filters,
    CampaignOption,
} from 'Modules/Plans/components/PlatformPosts/PlatformPostsFilters/PlatformPostsFilters';
import { useState } from 'react';
import { useProjectCampaigns } from '../../hooks/useProjectCampaigns';
import useProjectDetails from '../../useProjectDetails';
import range from 'lodash/range';
import { MessageArea } from '@round/whitelabel-ui';
import styles from './TikTokTab.module.css';
import usePlatformPosts from 'Modules/Plans/hooks/queries/posts/usePlatformPosts';
import useOnScrolledPastThreshold from 'Hooks/scroll/useOnScrolledPastThreshold';
import { CountRangeLabel } from '@round/whitelabel-ui';

type Props = {
    campaignIds: number[];
};

const PAGE_SIZE = 50;

const TikTokPlatformPosts = ({ campaignIds }: Props) => {
    const [filters, setFilters] = useState<Filters>({});
    const { data: campaigns } = useProjectCampaigns('tiktok');
    const { project, brand } = useProjectDetails();
    const tiktokCampaigns = campaigns?.results ?? [];
    const campaignOptions: CampaignOption[] = tiktokCampaigns.map((c) => ({
        value: c.id,
        label: c.name,
        managedBy: c.managed_by,
        imageUrl: project?.image || '',
        brandName: brand?.name,
        projectName: project?.name,
    }));

    const { data, isLoading, errorMessage, status, userStatsCache, hasNextPage, isFetchingNextPage, fetchNextPage } =
        usePlatformPosts(
            {
                platform: 'tiktok',
                include_post_details: true,
                status: 'live',
                page_size: PAGE_SIZE,
                ...filters,
                campaign_id: filters.campaign_id || campaignIds.toString(),
            },
            // posts fetch even for unavailable project (e.g. no access to client)
            { enabled: !!project?.id && Boolean(filters.campaign_id?.length || campaignIds.length) }
        );

    const posts = data?.pages.flatMap((p) => p.results) ?? [];
    const hasPosts = Boolean(status === 'success' && posts.length);
    const handleSinglePlayback = useSinglePlayback();
    const onEndReached = useOnScrolledPastThreshold({ threshold: 150 });

    return (
        <>
            <PlatformPostsFilters filters={filters} onChange={setFilters} campaignOptions={campaignOptions} />
            <CountRangeLabel
                className={styles.countRange}
                pageParams={data?.pageParams as number[]}
                count={data?.pages[0]?.count}
                pageSize={PAGE_SIZE}
                isLoading={isLoading}
            />

            {((!hasPosts && !isLoading) || !!errorMessage) && (
                <MessageArea appearance={errorMessage ? 'error' : undefined}>
                    <span>{errorMessage || 'No posts yet.'}</span>
                </MessageArea>
            )}

            <PostsGrid
                className={styles.postsGrid}
                onScroll={onEndReached(() => {
                    if (hasNextPage && !isFetchingNextPage && !isLoading) {
                        fetchNextPage();
                    }
                })}
            >
                {posts.map((post) => (
                    <PlatformPost
                        key={post.id}
                        data={mapTikTokPostToPlatformPostData(
                            post,
                            userStatsCache.find((c) => c.user_id === post.tiktok_details?.account_id)
                        )}
                        onPlay={handleSinglePlayback}
                    />
                ))}

                {(isLoading || isFetchingNextPage) &&
                    range(0, PAGE_SIZE).map((index) => <PlatformPost.LoadingView key={index} />)}
            </PostsGrid>
        </>
    );
};

export default TikTokPlatformPosts;
